import React, { useCallback, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Editor } from '@tinymce/tinymce-react';
import { useAuthContext } from 'AuthContext';
import { useMeetingHubContext } from '../MeetingHubContext';
import Modal from 'components/shared/Modal';
import { defaultTinyMCEProps } from 'util/form';
import MeetingService from 'services/MeetingService';
import { defaultSnackbarErrorProps } from 'util/layout';
import { createImagesUploadHandler } from 'util/meeting';

const ConferenceInfoSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { isStaff } = useAuthContext();
  const { meetingId, meetingSession, loadingMeetingSession, refetchMeetingSession } = useMeetingHubContext();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const editorRef = useRef(null);

  const handleEditClick = useCallback(() => {
    setEditModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setEditModalOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    const editor: any = editorRef.current;
    if (editor) {
      const content = editor.getContent();
      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId: meetingId,
        conferenceInfo: content,
      })
        .then(() => {
          enqueueSnackbar(`Updated conference info`, { variant: 'success' });
          refetchMeetingSession;
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to update conference info`, defaultSnackbarErrorProps);
          console.error('Error updating conference info:', error);
        });
    }
    setEditModalOpen(false);
  }, [enqueueSnackbar, meetingId, meetingSession, refetchMeetingSession]);

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Web & Conference Info</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{ typography: 'body1' }}
            dangerouslySetInnerHTML={{ __html: meetingSession?.conferenceInfo ?? '' }}
          />
        </AccordionDetails>
        {isStaff && (
          <AccordionActions>
            <Button variant="contained" onClick={handleEditClick}>
              Edit
            </Button>
          </AccordionActions>
        )}
      </Accordion>
      {isStaff && (
        <Modal
          title={'Edit Web & Conference Info'}
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSubmit={handleSave}
          actions={
            <>
              <Button
                color={'secondary'}
                variant={'outlined'}
                disabled={loadingMeetingSession}
                onClick={handleModalClose}
              >
                Cancel
              </Button>
              <Button type="submit" color={'primary'} variant={'contained'} disabled={loadingMeetingSession}>
                Save
              </Button>
            </>
          }
        >
          <Editor
            {...defaultTinyMCEProps}
            id="conference-info-editor"
            initialValue={meetingSession?.conferenceInfo}
            onInit={(_evt: any, editor: any) => (editorRef.current = editor)}
            init={{
              ...defaultTinyMCEProps.init,
              height: 400,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default ConferenceInfoSection;
